import React, { useCallback, useEffect, useState } from 'react';
import type { ClassNameProp } from '../common-types';
import { Maskable } from '../basic';
import { useTrackElementResize } from '../../lib';

export interface AsyncContentProps extends ClassNameProp {
  className?: string;
  stateUrl: string;
  operation: string;
  decorateHtml?: boolean;
}

export function AsyncContent({
  className,
  stateUrl,
  operation,
  decorateHtml,
  ...props
}: Readonly<AsyncContentProps>): React.JSX.Element {
  const ref = React.useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState('0px');

  const onLoad = () => {
    try {
      if (decorateHtml && ref.current) {
        const iframeDoc =
          ref.current.contentDocument || ref.current.contentWindow?.document;
        if (iframeDoc) {
          Array.from(document.styleSheets).forEach(styleSheet => {
            if (!styleSheet.href) {
              return;
            }
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = styleSheet.href;
            iframeDoc.head.appendChild(link);
          });
        }
      }
    } finally {
      // give the content some time to render before determining the height in the next step
      setTimeout(() => setIframeLoaded(true), 100);
    }
  };

  useEffect(() => {
    if (iframeLoaded) {
      setIframeHeight(
        ref?.current?.contentWindow?.document.body.scrollHeight + 'px'
      );
    }
  }, [iframeLoaded]);

  const resizeCallback = useCallback(() => {
    const scrollHeight =
      ref.current?.contentWindow?.document?.body?.scrollHeight;
    if (scrollHeight) {
      setIframeHeight(scrollHeight + 'px');
    }
  }, []);

  useTrackElementResize(ref, iframeLoaded, resizeCallback);

  return (
    <Maskable
      masked={!iframeLoaded}
      className={
        'ehs-async-content-iframe-container' +
        (className ? ' ' + className : '')
      }
    >
      <iframe
        {...props}
        ref={ref}
        onLoad={onLoad}
        src={stateUrl + '&operation=' + operation}
        height={iframeHeight}
        sandbox="allow-same-origin"
        style={{ display: iframeLoaded ? 'flex' : 'none' }}
      />
    </Maskable>
  );
}
